import { FGCustomInput, FGListen, FGSelectInput } from "nsitools-react";
import * as React from "react";
import * as Yup from "yup";

import { BreedGridDto, BreedJudgeApi, EJudgeType, SaveBreedJudgeDto } from "../../../../../api";
import { SmallFormGenerator, FGSRSHDateMaskInput } from "../../../../../components";
import { useApiService, useCrudApi, useSaveApi, useTl } from "../../../../../hooks";
import { useReferential } from "../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../locales";

export interface IPersonBreedJudgeSavePanelProps {
  onSaved: () => void;
  breed?: BreedGridDto;
  breedJudgeId?: number;
  personId: number;
}

export const PersonBreedJudgeSavePanel: React.FunctionComponent<IPersonBreedJudgeSavePanelProps> = ({
  onSaved,
  breed,
  breedJudgeId,
  personId,
}) => {
  const { t, tUnsafe } = useTl();
  const api = useApiService(BreedJudgeApi);
  const [judgeType, judgeTypeLoading] = useReferential((a) => a.getJudgeType(), false);
  const [judgeTypeItems, setJudgeTypeItems] = React.useState([]);

  const { data, loading, saving, saveItem } = useCrudApi<SaveBreedJudgeDto>(
    React.useMemo(
      () => ({
        getApiFn: async () => {
          if (!!breedJudgeId) {
            return await api.getBreedJudge(breedJudgeId);
          }
          return new SaveBreedJudgeDto({
            breedId: breed.breedId,
            judgeId: null,
            personId: personId,
            judgeType: null,
            internshipDate: null,
            nominationDate: null,
            excludedFrom: null,
            excludedTo: null,
          });
        },
        saveApiFn: async (data: SaveBreedJudgeDto) => {
          data.personId = personId;
          await api.saveBreedJudge(data);
          onSaved();
        },
        deleteApiFn: () => {},
      }),
      [api, breed, breedJudgeId, onSaved, personId]
    )
  );

  React.useEffect(() => {
    if (!judgeTypeLoading && judgeType) {
      const items = judgeType.map((c) => {
        return { label: tUnsafe(c.label), value: c.value };
      });
      setJudgeTypeItems(items);
    }
  }, [judgeType, judgeTypeLoading, tUnsafe]);

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      judgeType: Yup.string().nullable().required(t(ETLCodes.Required)).oneOf(Object.keys(EJudgeType)),
      internshipDate: Yup.date()
        .nullable()
        .typeError(t(ETLCodes.RequiredDate))
        .when("judgeType", (value, schema) =>
          value === EJudgeType.Internship ? schema.required(t(ETLCodes.RequiredDate)) : schema.nullable()
        ),
      nominationDate: Yup.date()
        .nullable()
        .typeError(t(ETLCodes.RequiredDate))
        .when("judgeType", (value, schema) => {
          return value === EJudgeType.Nominated ? schema.required(t(ETLCodes.RequiredDate)) : schema.nullable();
        }),
      excludedFrom: Yup.date().nullable().typeError(t(ETLCodes.InvalidDate)).nullable(),
      excludedTo: Yup.date()
        .nullable()
        .typeError(t(ETLCodes.RequiredDate))
        .when("excludedFrom", (value, schema) => {
          return value ? schema.required(t(ETLCodes.RequiredDate)) : schema.nullable();
        }),
    });
  }, [t]);

  const [judgeTypeState, setjudgeType] = React.useState({});

  return (
    <SmallFormGenerator
      onSubmit={saveItem}
      initialValues={data}
      loading={loading}
      editMode={true}
      saving={saving}
      validationSchema={FormSchema}
    >
      <FGListen field="judgeType" onChanged={(value) => setjudgeType(value)} />
      <FGCustomInput name="breed" label={t(ETLCodes.Breed)}>
        {(ctx) => <span>{!!breed ? breed.name : data?.breedName}</span>}
      </FGCustomInput>
      <FGSelectInput name="judgeType" label={t(ETLCodes.JudgeType)} items={judgeTypeItems} loading={judgeTypeLoading} />
      <FGSRSHDateMaskInput
        requiredMark={judgeTypeState === EJudgeType.Internship}
        name="internshipDate"
        label={t(ETLCodes.JudgeDateInternship)}
      />
      <FGSRSHDateMaskInput
        requiredMark={judgeTypeState === EJudgeType.Nominated}
        name="nominationDate"
        label={t(ETLCodes.JudgeDateNomination)}
      />
      <FGSRSHDateMaskInput name="excludedFrom" label={t(ETLCodes.JudgeDateExcludedFrom)} />
      <FGSRSHDateMaskInput name="excludedTo" label={t(ETLCodes.JudgeDateExcludedTo)} />
    </SmallFormGenerator>
  );
};
